import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input, Textarea, InputLabel, InputGroup } from '../../../components/ui';
import BookPicker from '../../../components/BookPicker';

export default function CoursesEdit({ course, available_books }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: course.title,
    description: course.description || '',
    starts_on: course.starts_on || '',
    ends_on: course.ends_on || '',
    library_item_ids: course.library_item_ids || [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    router.patch(`/teacher/courses/${course.id}`, { course: formData }, {
      onFinish: () => setIsSubmitting(false),
    });
  };

  const handleDelete = () => {
    if (confirm(t('teacherCourseEdit.deleteConfirm'))) {
      router.delete(`/teacher/courses/${course.id}`);
    }
  };

  return (
    <TeacherShell scrollTitle={<><Link href={`/teacher/courses/${course.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{course.title}</Link>{`  ›  ${t('common.edit')}`}</>}>
      <TeacherPageHeader
        title={t('teacherCourseEdit.title')}
        subtitle={course.title}
        backLink={`/teacher/courses/${course.id}`}
        backLabel={t('teacherCourseEdit.backToCourse')}
      />

      <div className="max-w-2xl">
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <InputGroup>
              <InputLabel htmlFor="title" required>{t('teacherCourseEdit.titleField')}</InputLabel>
              <Input id="title" value={formData.title} onChange={handleChange('title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="description">{t('teacherCourseEdit.description')}</InputLabel>
              <Textarea id="description" value={formData.description} onChange={handleChange('description')} rows={4} />
            </InputGroup>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputGroup>
                <InputLabel htmlFor="starts_on">{t('teacherCourseEdit.startDate')}</InputLabel>
                <Input
                  id="starts_on"
                  type="date"
                  value={formData.starts_on}
                  onChange={handleChange('starts_on')}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel htmlFor="ends_on">{t('teacherCourseEdit.endDate')}</InputLabel>
                <Input
                  id="ends_on"
                  type="date"
                  value={formData.ends_on}
                  onChange={handleChange('ends_on')}
                  min={formData.starts_on || undefined}
                />
              </InputGroup>
            </div>

            {available_books && available_books.length > 0 && (
              <BookPicker
                books={available_books}
                selectedIds={formData.library_item_ids}
                onChange={(ids) => setFormData((prev) => ({ ...prev, library_item_ids: ids }))}
              />
            )}

            <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="submit" variant="primary" disabled={isSubmitting || !formData.title.trim()}>
                {isSubmitting ? t('common.saving') : t('teacherCourseEdit.saveChanges')}
              </Button>
              <Link href={`/teacher/courses/${course.id}`}>
                <Button type="button" variant="ghost">{t('common.cancel')}</Button>
              </Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('teacherCourseEdit.deleteWarning')}
            </p>
            <Button variant="danger" size="sm" onClick={handleDelete}>{t('teacherCourseEdit.deleteCourse')}</Button>
          </div>
        </Card>
      </div>
    </TeacherShell>
  );
}
